import { computed, ref, type ComputedRef, type Ref } from 'vue';

import { http } from '@/interceptors/axiosRequestInterceptor';

/**
 * Talk to ELSA — the conversation on an order (AB#21422).
 *
 * One thread per order item, shared by everyone who can open the order. The
 * server keeps it; this keeps the copy on screen and the two transient states
 * a chat has (a question in flight, a failure under it).
 *
 * The shape of an answer is the backend's contract: plain text with [n]
 * markers into `evidence`, and `drafts` the person may insert. Nothing here
 * sends a draft anywhere — the card's buttons hand the text to the portal's
 * own actions.
 */

export type EvidenceSource =
  | 'order'
  | 'timeline'
  | 'comment'
  | 'ticket'
  | 'invoice'
  | 'tracking'
  | 'related_order'
  | 'read'
  | 'measured';

export interface Evidence {
  n: number;
  source: EvidenceSource | string;
  label: string;
  quote: string;
}

export type DraftKind = 'customer_reply' | 'internal_comment' | 'factory_message';

export interface Draft {
  kind: DraftKind | string;
  title: string;
  text: string;
}

export interface ConversationMessage {
  id: number;
  role: 'user' | 'elsa';
  by: string;
  mine: boolean;
  text: string;
  evidence: Evidence[];
  drafts: Draft[];
  looked_at: Record<string, number>;
  at: string | null;
  /** Client-only: the question that produced no answer, and why. */
  failed?: string | null;
}

interface ThreadPayload {
  messages?: ConversationMessage[];
  suggestions?: string[];
  available?: boolean;
}

interface AskPayload {
  question?: ConversationMessage;
  answer?: ConversationMessage | null;
  available?: boolean;
  error?: string;
}

export interface UseOrderConversation {
  messages: Ref<ConversationMessage[]>;
  suggestions: Ref<string[]>;
  available: Ref<boolean>;
  isLoading: Ref<boolean>;
  isAsking: Ref<boolean>;
  error: Ref<string | null>;
  isEmpty: ComputedRef<boolean>;
  load: () => Promise<void>;
  ask: (question: string) => Promise<void>;
  clear: () => Promise<void>;
}

let localId = -1;

export const useOrderConversation = (orderItemId: number): UseOrderConversation => {
  const messages = ref<ConversationMessage[]>([]);
  const suggestions = ref<string[]>([]);
  const available = ref(true);
  const isLoading = ref(false);
  const isAsking = ref(false);
  const error = ref<string | null>(null);

  const isEmpty = computed(() => messages.value.length === 0);

  const load = async (): Promise<void> => {
    isLoading.value = true;
    error.value = null;

    try {
      const { data } = await http.get(`order-items/${orderItemId}/conversation`);
      const payload = (data?.result ?? {}) as ThreadPayload;

      if (data?.success !== true) {
        error.value = data?.message || 'The conversation could not be loaded.';
        // A thread that cannot be read is a thread that cannot be asked into
        // — including a backend that predates the endpoint. Say so up front
        // rather than offering a box whose every question would fail.
        available.value = false;
        return;
      }

      messages.value = payload.messages ?? [];
      suggestions.value = payload.suggestions ?? [];
      if (payload.available !== undefined) available.value = payload.available;
    } catch (e: unknown) {
      error.value = messageOf(e, 'The conversation could not be loaded.');
      available.value = false;
    } finally {
      isLoading.value = false;
    }
  };

  const ask = async (question: string): Promise<void> => {
    const text = question.trim();
    if (text === '' || isAsking.value) return;

    // Show the question at once, under the asker's own name, so the thread
    // never looks as though nothing happened for the seconds ELSA takes.
    const pending: ConversationMessage = {
      id: localId--,
      role: 'user',
      by: 'You',
      mine: true,
      text,
      evidence: [],
      drafts: [],
      looked_at: {},
      at: new Date().toISOString(),
    };
    messages.value = [...messages.value, pending];
    isAsking.value = true;
    error.value = null;

    try {
      const { data } = await http.post(`order-items/${orderItemId}/conversation`, { message: text });
      const payload = (data?.result ?? {}) as AskPayload;

      const rest = messages.value.filter((m) => m.id !== pending.id);
      const asked = payload.question ?? pending;

      if (payload.available !== undefined) available.value = payload.available;

      if (payload.answer) {
        messages.value = [...rest, asked, payload.answer];
        suggestions.value = suggestions.value.filter((s) => s !== text);
      } else {
        const why = payload.error || data?.message || 'ELSA could not answer that just now.';
        messages.value = [...rest, { ...asked, failed: why }];
        error.value = why;
      }
    } catch (e: unknown) {
      const why = messageOf(e, 'ELSA could not answer that just now.');
      messages.value = messages.value.map((m) => (m.id === pending.id ? { ...m, failed: why } : m));
      error.value = why;
    } finally {
      isAsking.value = false;
    }
  };

  const clear = async (): Promise<void> => {
    error.value = null;

    try {
      const { data } = await http.delete(`order-items/${orderItemId}/conversation`);

      if (data?.success === true) {
        messages.value = [];
        await load();
      } else {
        error.value = data?.message || 'The conversation could not be cleared.';
      }
    } catch (e: unknown) {
      error.value = messageOf(e, 'The conversation could not be cleared.');
    }
  };

  return { messages, suggestions, available, isLoading, isAsking, error, isEmpty, load, ask, clear };
};

/** The server's sentence when there is one; ours when there is not. */
const messageOf = (e: unknown, fallback: string): string => {
  const msg = (e as { response?: { data?: { message?: unknown } } })?.response?.data?.message;

  return typeof msg === 'string' && msg.trim() !== '' ? msg : fallback;
};
